/* eslint-disable no-continue */
import { v4 as uuidv4 } from 'uuid';
import {
  ContentEntry,
  EntryType,
  OriginObjectType,
  Programme,
  ProgrammeEntry,
  ProgrammePlan,
  ProgrammePlanDay,
  RecommendationSource,
  SharedProgramme,
  Space,
  SpaceProductType,
} from '../API';
import { cloneObject } from './utils';
import { appSyncGraphQLApi } from '../app/smorgAppSyncGraphQLApi';
import {
  makeCopyOfProgramme,
  programmeObjectIDsOfType,
  programmeObjectIDsOfTypeInPlan,
  programmePlanDayIDFromDayIndex,
  programmePlanWithEntryAdded,
  PROGRAMMES_DEFAULT_TARGET_CALORIES,
  programmeWithPlanDaysUpdated,
  programmeWithPlanRemoved,
  programmeWithPlanUpdated,
} from '../services/programmes';
import { healthProGroupForSpaceID } from '../services/users';
import {
  calorieSplitsAreValid,
  DEFAULT_PROGRAMME_CALORIE_SPLITS,
  DEFAULT_PROGRAMME_MEAL_TYPES,
} from '../services/meal_types';
import {
  RECOMMENDER_ENTRY_TYPE_MEAL,
  RECOMMENDER_MEAL_SOURCE_SMORG,
} from '../services/recommender';
import { recommendMealsOperation } from './recommender_operations';
import { flattenObjects } from '../services/arrays';

/** Only for Meals and GRC recipes, for which we don't need to duplicate the object referred to by objectID */
export const copyProgrammePlanEntryToProgramme = async (
  entry: ProgrammeEntry,
  toProgrammeId: string,
  toProgrammePlanId: string,
  dayIndexes: Array<number>,
  lazyGetProgramme: ReturnType<
    typeof appSyncGraphQLApi.useLazyGetProgrammeQuery
  >[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
) => {
  const { data: toProgramme } = await lazyGetProgramme(toProgrammeId);
  if (!toProgramme) {
    return null;
  }
  const toPlan = toProgramme.plans.find((pl) => pl.id === toProgrammePlanId);
  if (!toPlan) {
    return null;
  }
  const dayIDs = dayIndexes.map((dayIndex) =>
    programmePlanDayIDFromDayIndex(dayIndex, toPlan.days),
  );

  const updatedPlan = programmePlanWithEntryAdded(
    toPlan,
    dayIDs,
    uuidv4(),
    entry.programmeEntryType,
    entry.objectID,
  );
  const updatedProgramme = programmeWithPlanUpdated(toProgramme, updatedPlan);
  return updateProgramme(updatedProgramme);
};

export const copyProgrammePlanDayToProgramme = async (
  fromDay: ProgrammePlanDay,
  toProgrammeId: string,
  toProgrammePlanId: string,
  dayIndexes: Array<number>,
  currentHealthProGroup: string | null | undefined,
  lazyGetProgramme: ReturnType<
    typeof appSyncGraphQLApi.useLazyGetProgrammeQuery
  >[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
  lazyGetContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useLazyGetContentEntryQuery
  >[0],
  createContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useCreateContentEntryMutation
  >[0],
) => {
  const { data: toProgramme } = await lazyGetProgramme(toProgrammeId);
  if (!toProgramme) {
    return;
  }
  const entriesToCopy = fromDay.entries.filter((entry) =>
    [EntryType.MEAL, EntryType.GRC_RECIPE, EntryType.CONTENT_ENTRY].includes(
      entry.programmeEntryType,
    ),
  );
  const toPlan = toProgramme.plans.find((pl) => pl.id === toProgrammePlanId);
  if (!toPlan) {
    return;
  }
  const toDayIDs = dayIndexes.map((dayIndex) =>
    programmePlanDayIDFromDayIndex(dayIndex, toPlan.days),
  );

  const contentEntryIDsToClone = entriesToCopy
    .filter((entry) => entry.programmeEntryType === EntryType.CONTENT_ENTRY)
    .map((entry) => entry.objectID);

  const clonedContentEntries = await Promise.all(
    contentEntryIDsToClone.map(async (contentEntryID) => {
      const contentEntry = await lazyGetContentEntry(contentEntryID);
      const newContentEntry = cloneObject(contentEntry);
      const input = {
        ...newContentEntry,
        parentID: toProgrammeId,
        groups: currentHealthProGroup ? [currentHealthProGroup] : null,
      };
      const { data: createdContentEntry } = await createContentEntry(input);
      return createdContentEntry;
    }),
  );

  let updatedPlan = toPlan;

  // eslint-disable-next-line no-restricted-syntax,no-continue
  for (const entry of entriesToCopy) {
    if (
      ![EntryType.MEAL, EntryType.CONTENT_ENTRY].includes(
        entry.programmeEntryType,
      )
    ) {
      continue;
    }
    if (entry.programmeEntryType === EntryType.MEAL) {
      const newEntryID = uuidv4();
      updatedPlan = programmePlanWithEntryAdded(
        updatedPlan,
        toDayIDs,
        newEntryID,
        entry.programmeEntryType,
        entry.objectID,
      );
    }
    if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
      const contentEntryIndex = contentEntryIDsToClone.indexOf(entry.objectID);
      if (contentEntryIndex < 0) {
        continue;
      }
      const copiedContentEntry = clonedContentEntries[contentEntryIndex];
      if (!copiedContentEntry) {
        continue;
      }
      const newEntryID = uuidv4();
      updatedPlan = programmePlanWithEntryAdded(
        updatedPlan,
        toDayIDs,
        newEntryID,
        entry.programmeEntryType,
        copiedContentEntry.id,
      );
    }
  }

  const updatedProgramme = programmeWithPlanUpdated(toProgramme, updatedPlan);

  await updateProgramme(updatedProgramme);
};

export const programmePlanEntryQuickDuplicate = async (
  programme: Programme,
  programmePlanId: string,
  entry: ProgrammeEntry,
  currentHealthProGroup: string | null | undefined,
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
  lazyGetContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useLazyGetContentEntryQuery
  >[0],
  createContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useCreateContentEntryMutation
  >[0],
) => {
  let newObjectID: string | null = null;
  //     if (entry.programmeEntryType === EntryType.NOTE) {
  //       const clonedNote = cloneObject(
  //         getState().notes[entry.objectID],
  //       ) as unknown as CreateNoteInput;
  //       console.log({ clonedNote });
  //       const insertedNote = await addNoteOperation(
  //         programme.id,
  //         clonedNote,
  //         null,
  //         currentHealthProGroup,
  //       );
  //       newObject = insertedNote;
  //     }
  if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
    const { data: contentEntry } = await lazyGetContentEntry(entry.objectID);
    const clonedContentEntry = cloneObject(contentEntry);
    console.log({ clonedContentEntry });
    const input = { ...clonedContentEntry };
    input.parentID = programme.id;
    input.groups = currentHealthProGroup ? [currentHealthProGroup] : null;
    const { data: insertedContentEntry } = await createContentEntry(input);
    if (insertedContentEntry) {
      newObjectID = insertedContentEntry.id;
    }
  }
  const plan = programme.plans.find((pl) => pl.id === programmePlanId);
  if (!plan) {
    return null;
  }
  const updatedPlanDays = plan.days.map((day) => {
    const entryIndexToDuplicate = day.entries.findIndex(
      (e) => e.id === entry.id,
    );
    if (entryIndexToDuplicate === -1) {
      return day;
    }
    const newEntry = {
      id: uuidv4(),
      programmeEntryType: entry.programmeEntryType,
      objectID: newObjectID || entry.objectID,
    } as ProgrammeEntry;
    const updatedEntries = [...day.entries];
    updatedEntries.splice(entryIndexToDuplicate, 0, newEntry);
    return {
      ...day,
      entries: updatedEntries,
    };
  });
  const updatedPlan = {
    ...plan,
    days: updatedPlanDays,
  };
  const updatedProgramme = programmeWithPlanUpdated(programme, updatedPlan);
  return updateProgramme(updatedProgramme);
};

export const removeMealsAndGRCRecipesFromProgrammePlan = async (
  programme: Programme,
  programmePlanId: string,
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
) => {
  const plan = programme.plans.find((pl) => pl.id === programmePlanId);
  if (!plan) {
    return null;
  }
  const updatedPlanDays = plan.days.map((day) => {
    const updatedEntries = day.entries.filter(
      (e) =>
        e.programmeEntryType !== 'GRC_RECIPE' &&
        e.programmeEntryType !== 'MEAL',
    );
    return {
      ...day,
      entries: updatedEntries,
    };
  });
  const updatedPlan = {
    ...plan,
    days: updatedPlanDays,
  };
  const updatedProgramme = programmeWithPlanUpdated(programme, updatedPlan);
  return updateProgramme(updatedProgramme);
};

export const removeMealsAndGRCRecipesFromProgrammePlanDay = async (
  programme: Programme,
  programmePlanId: string,
  dayId: string,
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
) => {
  const plan = programme.plans.find((pl) => pl.id === programmePlanId);
  if (!plan) {
    return null;
  }
  const updatedPlanDays = plan.days.map((day) => {
    if (day.id !== dayId) {
      return day;
    }
    const updatedEntries = day.entries.filter(
      (e) =>
        e.programmeEntryType !== 'GRC_RECIPE' &&
        e.programmeEntryType !== 'MEAL',
    );
    return {
      ...day,
      entries: updatedEntries,
    };
  });
  const updatedPlan = {
    ...plan,
    days: updatedPlanDays,
  };
  const updatedProgramme = programmeWithPlanUpdated(programme, updatedPlan);
  return updateProgramme(updatedProgramme);
};

export const removePlan = async (
  programme: Programme,
  programmePlanId: string,
  deleteNote: ReturnType<typeof appSyncGraphQLApi.useDeleteNoteMutation>[0],
  deleteContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useDeleteContentEntryMutation
  >[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
) => {
  const planIndex = programme.plans.findIndex(
    (pl) => pl.id === programmePlanId,
  );
  const noteIDs = programmeObjectIDsOfTypeInPlan(
    programme,
    planIndex,
    EntryType.NOTE,
  );
  const contentEntryIDs = programmeObjectIDsOfTypeInPlan(
    programme,
    planIndex,
    EntryType.CONTENT_ENTRY,
  );
  await Promise.all([
    ...noteIDs.map((noteID) =>
      deleteNote({ id: noteID, parentID: programme.id }),
    ),
    ...contentEntryIDs.map((contentEntryID) =>
      deleteContentEntry({ id: contentEntryID, parentID: programme.id }),
    ),
  ]);
  const updatedProgramme = programmeWithPlanRemoved(programme, programmePlanId);
  return updateProgramme(updatedProgramme);
};

export const removeProgramme = async (
  programme: Programme,
  deleteNote: ReturnType<typeof appSyncGraphQLApi.useDeleteNoteMutation>[0],
  deleteContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useDeleteContentEntryMutation
  >[0],
  deleteProgramme: ReturnType<
    typeof appSyncGraphQLApi.useDeleteProgrammeMutation
  >[0],
) => {
  const noteIDs = programmeObjectIDsOfType(programme, EntryType.NOTE);
  const contentEntryIDs = programmeObjectIDsOfType(
    programme,
    EntryType.CONTENT_ENTRY,
  );
  // TODO also unpublish??
  await Promise.all([
    ...noteIDs.map((noteID) =>
      deleteNote({ id: noteID, parentID: programme.id }),
    ),
    ...contentEntryIDs.map((contentEntryID) =>
      deleteContentEntry({ id: contentEntryID, parentID: programme.id }),
    ),
  ]);
  return deleteProgramme(programme.id);
};

export const publishLatestVersionOfProgramme = async (
  programme: Programme,
  sharedProgrammes: Array<SharedProgramme>,
  linkedContentEntries: Record<string, ContentEntry>,
  space: Space,
  userLocale: string,
  createSharedContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useCreateSharedContentEntryMutation
  >[0],
  createSharedProgramme: ReturnType<
    typeof appSyncGraphQLApi.useCreateSharedProgrammeMutation
  >[0],
  updateSharedProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateSharedProgrammeMutation
  >[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
  updateSpace: ReturnType<typeof appSyncGraphQLApi.useUpdateSpaceMutation>[0],
) => {
  const sharedProgrammePrevVersion = sharedProgrammes.find(
    (sp) => sp.programmeID === programme.id,
  );

  let nextVersionStr = '1';
  if (sharedProgrammePrevVersion) {
    const currentVersionInt = parseInt(sharedProgrammePrevVersion.version, 10);
    nextVersionStr = (currentVersionInt + 1).toString();
  }
  const currentHealthProGroup = healthProGroupForSpaceID(space.id);
  let createdSharedProgramme: SharedProgramme | null | undefined = null;
  const input = {
    spaceID: space.id,
    programmeID: programme.id,
    title: programme.title,
    shortDescription: programme.shortDescription,
    description: programme.description,
    coverImageUrl: programme.coverImageUrl,
    plans: programme.plans,
    nutritionConstraints: programme.nutritionConstraints,
    copyMealsExactly: programme.copyMealsExactly,
    showNutritionToUsers: programme.showNutritionToUsers,
    personalisedMealScaling: programme.personalisedMealScaling,
    recipesBoard: programme.recipesBoard,
    databaseRecipesBoard: programme.databaseRecipesBoard,
    sharedRecipesBoardIDs: /* sharedAssociatedRecipesBoardIDs */ [],
    recipesBoardIDs: programme.recipesBoardIDs,
    onboardingConfiguration: programme.onboardingConfiguration,
    locales: programme.locales || [userLocale],
    mealTypes: programme.mealTypes,
    calorieSplits: programme.calorieSplits,
    categoryTags: programme.categoryTags,
    version: nextVersionStr,
    availableInMembershipTierIDs: programme.availableInMembershipTierIDs,
    groups: [currentHealthProGroup],
  };
  if (sharedProgrammePrevVersion?.id) {
    createdSharedProgramme = (
      await updateSharedProgramme({
        ...input,
        id: sharedProgrammePrevVersion?.id,
      })
    ).data;
  } else {
    createdSharedProgramme = (await createSharedProgramme(input)).data;
  }
  if (!createdSharedProgramme) {
    // throw new Error('Could not create shared programme');
    return null;
  }

  // TODO remove owned entities too
  const contentEntryIDs = Object.keys(linkedContentEntries);
  const contentEntryPromises =
    createdSharedProgramme &&
    Object.values(linkedContentEntries).map(async (contentEntry) => {
      const sharedContentEntryInput = {
        parentID: createdSharedProgramme?.id || '',
        title: contentEntry.title,
        body: contentEntry.body,
        coverImageUrl: contentEntry.coverImageUrl,
        isCoverImageShownInDetailView:
          contentEntry.isCoverImageShownInDetailView,
        tags: contentEntry.tags,
        origin: {
          originObjectType: OriginObjectType.CONTENT_ENTRY,
          originObjectID: contentEntry.id,
        },
      };
      const { data: createdSharedContentEntry } =
        await createSharedContentEntry(sharedContentEntryInput);
      return createdSharedContentEntry;
    });
  const sharedContentEntries = await Promise.all(contentEntryPromises);
  const sharedProgrammeToUpdate = {
    ...createdSharedProgramme,
    plans: createdSharedProgramme.plans.map(
      (plan) =>
        ({
          ...plan,
          days: plan.days.map((day) => ({
            ...day,
            entries: day.entries
              .map((entry) => {
                if (entry.programmeEntryType === EntryType.GRC_RECIPE) {
                  // GRC recipe entries pass through unchanged
                  return entry;
                }

                if (entry.programmeEntryType === EntryType.MEAL) {
                  return entry;
                }

                if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
                  const sourceContentEntryID = entry.objectID;
                  const index = contentEntryIDs.indexOf(sourceContentEntryID);
                  if (index === -1) {
                    return null;
                  }
                  const sharedContentEntry = sharedContentEntries[index];
                  if (!sharedContentEntry) {
                    console.warn(
                      `Could not find shared content entry for content entry ID ${sourceContentEntryID}`,
                    );
                    return null;
                  }
                  return {
                    ...entry,
                    objectID: sharedContentEntry.id,
                  };
                }

                if (
                  entry.programmeEntryType ===
                  EntryType.SHARED_CONTENT_ENTRY_REFERENCE
                ) {
                  return entry;
                }

                // Other entry types do not pass through
                return null;
              })
              .filter((entry) => !!entry),
          })),
        } as ProgrammePlan),
    ),
  };

  const { data: updatedSharedProgramme } = await updateSharedProgramme(
    sharedProgrammeToUpdate,
  );
  if (!updatedSharedProgramme) {
    return createdSharedProgramme;
  }
  const updatedProgramme = {
    ...programme,
    shareRecords: [
      ...(programme.shareRecords || []),
      {
        sharedProgrammeID: updatedSharedProgramme.id,
        version: nextVersionStr,
        updatedOn: updatedSharedProgramme.updatedAt,
      },
    ],
  };
  await updateProgramme(updatedProgramme);
  const sharedProgrammesToPreview = sharedProgrammes.filter(
    (sp) => sp.spaceID === space.id,
  );
  const updatedSpace = {
    ...space,
    productPreviewSections: [
      {
        title: 'Available Programs',
        productPreviews: sharedProgrammesToPreview.map((sp) => ({
          productType: SpaceProductType.PROGRAMME,
          productObjectID: sp.id,
          title: sp.title,
          shortDescription: sp.shortDescription,
          coverImageUrl: sp.coverImageUrl,
          availableInMembershipTierIDs: sp.availableInMembershipTierIDs,
        })),
      },
    ],
  };
  await updateSpace(updatedSpace);
  return updatedSharedProgramme;
};

export const unpublishProgramme = async (
  programmeID: string,
  sharedProgrammes: Array<SharedProgramme>,
  space: Space,
  deleteSharedProgramme: ReturnType<
    typeof appSyncGraphQLApi.useDeleteSharedProgrammeMutation
  >[0],
  updateSpace: ReturnType<typeof appSyncGraphQLApi.useUpdateSpaceMutation>[0],
) => {
  // TODO remove owned entities too
  const sharedProgramme = sharedProgrammes.find(
    (sp) => sp.programmeID === programmeID,
  );
  if (!sharedProgramme) {
    return null;
  }
  const sharedProgrammesToPreview = sharedProgrammes.filter(
    (sp) => sp.spaceID === space.id,
  );
  const updatedSpace = {
    ...space,
    productPreviewSections: [
      {
        title: 'Available Programs',
        productPreviews: sharedProgrammesToPreview.map((sp) => ({
          productType: SpaceProductType.PROGRAMME,
          productObjectID: sp.id,
          title: sp.title,
          shortDescription: sp.shortDescription,
          coverImageUrl: sp.coverImageUrl,
          availableInMembershipTierIDs: sp.availableInMembershipTierIDs,
        })),
      },
    ],
  };
  await updateSpace(updatedSpace);
  return deleteSharedProgramme(sharedProgramme.id);
};

export const programmePlanRecommendMeals = async (
  programme: Programme,
  programmePlanId: string,
  numDaysToPlan: number,
  startDayIndexOneBased: number,
  userLocale: string | null | undefined,
  lazyGetFullMealsByParents: ReturnType<
    typeof appSyncGraphQLApi.useLazyGetFullMealsByParentsQuery
  >[0],
  updateMeal: ReturnType<typeof appSyncGraphQLApi.useUpdateMealMutation>[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
  // eslint-disable-next-line no-unused-vars
  notifCb: (notificationText: string) => void,
) => {
  const plan = programme.plans.find((p) => p.id === programmePlanId);
  if (!plan) {
    return;
  }
  const { personalisedMealScaling } = programme;
  const recipesBoardsIDs = programme.recipesBoardIDs || [];
  const { data: fullMealsByParents } = await lazyGetFullMealsByParents(
    recipesBoardsIDs,
  );
  const fullMeals = flattenObjects(Object.values(fullMealsByParents || {}));
  const existingDays = plan.days.map((day, indexZeroBased) => ({
    dayIndex: indexZeroBased + 1,
    entries: day.entries
      .filter((entry) => [EntryType.MEAL].includes(entry.programmeEntryType))
      .filter(
        (entry) =>
          entry.programmeEntryType === EntryType.MEAL &&
          !!(fullMeals[entry.objectID]?.recipes || [])[0],
      )
      .map((entry) => ({
        entryType: RECOMMENDER_ENTRY_TYPE_MEAL,
        mealType: ((fullMeals[entry.objectID]?.recipes || [])[0].mealTypes ||
          [])[0],
        meals: [
          {
            mealSource: RECOMMENDER_MEAL_SOURCE_SMORG,
            id: entry.objectID,
            servings: fullMeals[entry.objectID].recipes[0]?.servings || 1,
            categoryTags: [],
            scaleFactor: 1.0,
          },
        ],
      })),
  }));

  const programmeHasValidMealTypesAndCalorieSplits =
    (programme.mealTypes || []).length > 0 &&
    calorieSplitsAreValid(programme.mealTypes, programme.calorieSplits);

  const mealTypes = programmeHasValidMealTypesAndCalorieSplits
    ? programme.mealTypes
    : DEFAULT_PROGRAMME_MEAL_TYPES;

  const calorieSplits = programmeHasValidMealTypesAndCalorieSplits
    ? programme.calorieSplits
    : DEFAULT_PROGRAMME_CALORIE_SPLITS;

  try {
    const recommendedMenu = await recommendMealsOperation(
      mealTypes,
      numDaysToPlan,
      existingDays,
      startDayIndexOneBased,
      programme.nutritionConstraints,
      {},
      recipesBoardsIDs,
      calorieSplits,
      1.0,
      personalisedMealScaling,
      [RecommendationSource.SMORG],
      PROGRAMMES_DEFAULT_TARGET_CALORIES,
      programme.locales || [userLocale],
    );
    console.log(JSON.stringify(recommendedMenu));

    if (recommendedMenu.errorMessage) {
      console.warn(recommendedMenu.errorMessage);
      const notificationText =
        recipesBoardsIDs.length === 0
          ? 'Not enough recipes are available to fit the plan. Check if enough recipes are associated with this program, or try relaxing the program constraints'
          : 'Something went wrong, please try again';
      if (notifCb) {
        notifCb(notificationText);
      }
      return;
    }

    // If personalised meal scaling, we don't reference the Smorg meal, instead we make
    // a copy of the Smorg meal and apply the scaled ingredients.
    const mealUpdateJobs = personalisedMealScaling
      ? []
      : recommendedMenu.days.flatMap((recommendedDay) =>
          recommendedDay.entries
            .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
            .flatMap((recommendedEntry) => {
              const mealTypeSlot = recommendedEntry.mealType;
              return recommendedEntry.meals
                .filter((m) => m.mealSource === RECOMMENDER_MEAL_SOURCE_SMORG)
                .map((m) => ({
                  meal: fullMeals[m.id],
                  mealTypeSlot,
                  scaledMealNumServings: m.servings,
                  scaledIngredientsFullText: m.scaledIngredientsFullText,
                  scaledNutrition: m.scaledNutrition,
                  scaledStructuredIngredients: m.scaledStructuredIngredients,
                  scaledDerivedIngredientNutrition:
                    m.scaledDerivedIngredientNutrition,
                }));
            }),
        );

    // const smorgMealImportJobs = personalisedMealScaling
    //   ? recommendedMenu.days.flatMap((recommendedDay) =>
    //       recommendedDay.entries
    //         .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
    //         .flatMap((recommendedEntry) => {
    //           const mealTypeSlot = recommendedEntry.mealType;
    //           return recommendedEntry.meals
    //             .filter((m) => m.mealSource === RECOMMENDER_MEAL_SOURCE_SMORG)
    //             .map((m) => ({
    //               meal: m,
    //               mealTypeSlot,
    //               scaledMealNumServings: m.servings,
    //               scaledIngredientsFullText: m.scaledIngredientsFullText,
    //               scaledNutrition: m.scaledNutrition,
    //               scaledStructuredIngredients: m.scaledStructuredIngredients,
    //               scaledDerivedIngredientNutrition:
    //                 m.scaledDerivedIngredientNutrition,
    //             }));
    //         }),
    //     )
    //   : [];

    // const smorgMealIDsToImport = smorgMealImportJobs.map((job) => job.meal.id);

    // const smorgImportedMeals = await Promise.all(
    //   smorgMealImportJobs.map((job) => {
    //     const meal = job.meal;
    //     return cloneMealWithScaling(
    //       meal,
    //       databaseRecipesBoardID,  // WARNING, need to put these scaled meals into the embedded recipesBoard but it will create duplicates!
    //       currentSpaceMembershipID,
    //       job.mealTypeSlot,
    //       job.scaledMealNumServings,
    //       job.scaledIngredientsFullText,
    //       job.scaledNutrition,
    //       job.scaledStructuredIngredients,
    //       job.scaledDerivedIngredientNutrition,
    //     );
    //   }),
    // );

    await Promise.all(
      mealUpdateJobs.map(async (job) => {
        const { meal } = job;
        if (!meal) {
          console.warn(`Could not find meal with ID ${job.meal.id} to update`);
          return null;
        }
        const mealToRewrite = {
          ...meal,
          recipes: [{ ...meal.recipes[0], mealTypes: [job.mealTypeSlot] }],
        };
        const { data: updatedMeal } = await updateMeal(mealToRewrite);
        return updatedMeal;
      }),
    );

    const recommendedMenuWithProgrammeEntries = {
      ...recommendedMenu,
      days: recommendedMenu.days.map((recommendedDay) => {
        const newEntries = recommendedDay.entries
          .flatMap((e) => e.meals)
          .map((recommendedMeal) => {
            // recommendedMeal.mealSource === RECOMMENDER_MEAL_SOURCE_SMORG
            // if (personalisedMealScaling) {
            //   const mealIDToReplace = recommendedMeal.id;
            //   const index = smorgMealIDsToImport.indexOf(mealIDToReplace);
            //   if (index === -1) {
            //     console.warn(
            //       `Could not find imported meal for meal ID ${mealIDToReplace}`,
            //     );
            //     return null;
            //   }
            //   return smorgImportedMeals[index].id;
            // }
            return recommendedMeal.id;
          })
          .filter((mealID) => mealID)
          .map(
            (mealID) =>
              ({
                id: uuidv4(),
                programmeEntryType: EntryType.MEAL,
                objectID: mealID,
              } as ProgrammeEntry),
          );
        return { ...recommendedDay, entries: newEntries };
      }),
    };

    const planDays = [...plan.days];
    recommendedMenuWithProgrammeEntries.days.forEach(
      (recommendedDay, indexZeroBased) => {
        const existingDayIndexZeroBased =
          startDayIndexOneBased - 1 + indexZeroBased;
        const planDay = { ...planDays[existingDayIndexZeroBased] };
        planDay.entries = [...planDay.entries, ...recommendedDay.entries];
        planDays[existingDayIndexZeroBased] = planDay;
      },
    );

    const updatedProgramme = programmeWithPlanDaysUpdated(
      programme,
      programmePlanId,
      planDays,
    );

    await updateProgramme(updatedProgramme);
  } catch (e) {
    console.warn(e);
    if (notifCb) {
      notifCb('Something went wrong, please try again');
    }
  }
};

export const duplicateProgramme = async (
  programme: Programme,
  linkedContentEntries: Record<string, ContentEntry>,
  createProgramme: ReturnType<
    typeof appSyncGraphQLApi.useCreateProgrammeMutation
  >[0],
  updateProgramme: ReturnType<
    typeof appSyncGraphQLApi.useUpdateProgrammeMutation
  >[0],
  createContentEntry: ReturnType<
    typeof appSyncGraphQLApi.useCreateContentEntryMutation
  >[0],
) => {
  const newProgramme = makeCopyOfProgramme(programme);
  const { data: createdProgramme } = await createProgramme(newProgramme);
  if (!createdProgramme) {
    return null;
  }
  const contentEntryIDs = Object.keys(linkedContentEntries);
  const contentEntryPromises =
    createdProgramme &&
    Object.values(linkedContentEntries).map(async (contentEntry) => {
      const contentEntryInput = {
        parentID: createdProgramme?.id || '',
        title: contentEntry.title,
        body: contentEntry.body,
        coverImageUrl: contentEntry.coverImageUrl,
        isCoverImageShownInDetailView:
          contentEntry.isCoverImageShownInDetailView,
        tags: contentEntry.tags,
        origin: {
          originObjectType: OriginObjectType.CONTENT_ENTRY,
          originObjectID: contentEntry.id,
        },
      };
      const { data: createdContentEntry } = await createContentEntry(
        contentEntryInput,
      );
      return createdContentEntry;
    });

  const contentEntries = await Promise.all(contentEntryPromises || []);
  const programmeUpdateInput = {
    ...createdProgramme,
    plans: createdProgramme.plans.map(
      (plan) =>
        ({
          ...plan,
          days: plan.days.map((day) => ({
            ...day,
            entries: day.entries
              .map((entry) => {
                if (entry.programmeEntryType === EntryType.GRC_RECIPE) {
                  // GRC recipe entries pass through unchanged
                  return entry;
                }

                if (entry.programmeEntryType === EntryType.MEAL) {
                  return entry;
                }

                if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
                  const sourceContentEntryID = entry.objectID;
                  const index = contentEntryIDs.indexOf(sourceContentEntryID);
                  if (index === -1) {
                    return null;
                  }
                  const newContentEntry = contentEntries[index];
                  if (!newContentEntry) {
                    console.warn(
                      `Could not find content entry for content entry ID ${sourceContentEntryID}`,
                    );
                    return null;
                  }
                  return {
                    ...entry,
                    objectID: newContentEntry.id,
                  };
                }

                if (
                  entry.programmeEntryType ===
                  EntryType.SHARED_CONTENT_ENTRY_REFERENCE
                ) {
                  return entry;
                }

                // Other entry types do not pass through
                return null;
              })
              .filter((entry) => !!entry),
          })),
        } as ProgrammePlan),
    ),
  };
  const { data: updatedProgramme } = await updateProgramme(
    programmeUpdateInput,
  );
  return updatedProgramme;
};
